/**
 * Alertes ouvertes posées sur la carte de flotte : arrêt critique hors zone,
 * camion hors itinéraire… Seules celles qui portent une position (dans
 * `metadata_json`) ont une place sur la carte ; les autres restent dans le
 * bandeau et la page des alertes.
 */
import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Marker, Popup } from 'react-leaflet';

import { alertIcon } from './icons';
import { formatDateHeure, formatIlYA } from '../../lib/formats';
import { libelle, texteSur } from '../../lib/libelles';

const EMPTY_ARRAY = [];

const nombre = (valeur) => {
  if (valeur === null || valeur === undefined || valeur === '') return null;
  const n = Number(valeur);
  return Number.isFinite(n) ? n : null;
};

/** Position d'une alerte, ou null si elle n'en porte pas. */
export const positionAlerte = (alerte) => {
  const meta = alerte?.metadata_json || alerte?.metadata || {};
  const lat = nombre(meta.latitude);
  const lng = nombre(meta.longitude);
  if (lat === null || lng === null || Math.abs(lat) > 90 || Math.abs(lng) > 180) return null;
  return [lat, lng];
};

export const alertesPlacees = (alertes) =>
  (Array.isArray(alertes) ? alertes : EMPTY_ARRAY)
    .map((alerte) => ({ alerte, position: positionAlerte(alerte) }))
    .filter((a) => a.position);

export function AlertePopup({ alerte, now = Date.now() }) {
  const titre = libelle('alerteEvent', alerte.event_type, texteSur(alerte.title, 'Alerte'));
  const tripId = alerte.entity_type === 'trip' ? alerte.entity_id : null;
  return (
    <div className="min-w-[200px] max-w-[240px] space-y-1 text-sm">
      <div className="font-bold">{titre}</div>
      {alerte.message && <p className="text-muted-foreground">{texteSur(alerte.message, '')}</p>}
      {alerte.created_at && (
        <p className="text-xs text-muted-foreground" title={formatDateHeure(alerte.created_at, '') || undefined}>
          Ouverte {formatIlYA(alerte.created_at, '', now)}
        </p>
      )}
      <div className={tripId ? 'grid grid-cols-2 gap-1.5 pt-1' : 'pt-1'}>
        {tripId && (
          <Link
            to={`/trips/${tripId}`}
            className="block rounded bg-info py-1 text-center text-sm !text-white hover:bg-info"
          >
            Voir le trajet
          </Link>
        )}
        <Link
          to="/alerts"
          className="block rounded border border-border bg-card py-1 text-center text-sm !text-foreground hover:bg-muted"
        >
          Alertes
        </Link>
      </div>
    </div>
  );
}

export default function AlertLayer({ alertes = EMPTY_ARRAY, now }) {
  const placees = useMemo(() => alertesPlacees(alertes), [alertes]);
  return placees.map(({ alerte, position }) => {
    const titre = libelle('alerteEvent', alerte.event_type, texteSur(alerte.title, 'Alerte'));
    return (
      <Marker
        key={alerte.id || `${alerte.event_type}-${position.join(',')}`}
        position={position}
        icon={alertIcon({ critique: alerte.severity === 'critical', description: titre })}
        zIndexOffset={-100}
      >
        <Popup>
          <AlertePopup alerte={alerte} now={now} />
        </Popup>
      </Marker>
    );
  });
}
